/**
 * Invoice issues (ALC-5182 §5): what stops a draft from going on, by the step it blocks. An
 * issue is opened when a check fails and resolved when it passes again; a step with an open
 * issue does not start. The API and the web read the copy from here, so a code cannot ship
 * without pt-BR and en text: `AR_INVOICE_ISSUE_MESSAGES` is a total `Record`.
 * Classify issues by `code`, never by message text.
 */
export const AR_INVOICE_ISSUE_STEPS = ['RECORD', 'APPROVE', 'NFSE'] as const;
export type ArInvoiceIssueStepCode = (typeof AR_INVOICE_ISSUE_STEPS)[number];

export const AR_INVOICE_ISSUE_CODES = [
  'SERVICE_NOT_MAPPED',
  'REVENUE_ACCOUNT_MISSING',
  'GL_PERIOD_CLOSED',
  'CUSTOMER_TAX_ID_INVALID',
  'CUSTOMER_TAX_ID_MISSING',
  'CUSTOMER_ADDRESS_INCOMPLETE',
  'SERVICE_FISCAL_CODE_MISSING',
] as const;
export type ArInvoiceIssueCode = (typeof AR_INVOICE_ISSUE_CODES)[number];

/** The step each issue code blocks. */
export const AR_INVOICE_ISSUE_STEP_BY_CODE: Record<ArInvoiceIssueCode, ArInvoiceIssueStepCode> = {
  SERVICE_NOT_MAPPED: 'APPROVE',
  REVENUE_ACCOUNT_MISSING: 'APPROVE',
  GL_PERIOD_CLOSED: 'APPROVE',
  CUSTOMER_TAX_ID_INVALID: 'NFSE',
  CUSTOMER_TAX_ID_MISSING: 'NFSE',
  CUSTOMER_ADDRESS_INCOMPLETE: 'NFSE',
  SERVICE_FISCAL_CODE_MISSING: 'NFSE',
};

export const AR_INVOICE_ISSUE_MESSAGES: Record<ArInvoiceIssueCode, { en: string; ptBR: string }> = {
  SERVICE_NOT_MAPPED: {
    en: 'A line names a service that is not registered. Register the service or pick an existing one on the line.',
    ptBR: 'Uma linha informa um serviço que não está cadastrado. Cadastre o serviço ou escolha um serviço existente na linha.',
  },
  REVENUE_ACCOUNT_MISSING: {
    en: 'The invoice has no accounting classification. Classify the invoice before approving it.',
    ptBR: 'A fatura não tem classificação contábil. Classifique a fatura antes de aprová-la.',
  },
  GL_PERIOD_CLOSED: {
    en: 'The accounting period of the invoice issue date is closed. Open the period or change the issue date.',
    ptBR: 'O período contábil da data de emissão da fatura está fechado. Abra o período ou altere a data de emissão.',
  },
  CUSTOMER_TAX_ID_INVALID: {
    en: "The customer's CPF or CNPJ is not valid, so the NFS-e cannot be issued. Fix the customer's CPF or CNPJ.",
    ptBR: 'O CPF ou CNPJ do cliente não é válido, então a NFS-e não pode ser emitida. Corrija o CPF ou CNPJ do cliente.',
  },
  CUSTOMER_TAX_ID_MISSING: {
    en: 'The customer is a company with no CNPJ, so the NFS-e cannot be issued. Add the CNPJ to the customer.',
    ptBR: 'O cliente é uma empresa sem CNPJ, então a NFS-e não pode ser emitida. Informe o CNPJ do cliente.',
  },
  CUSTOMER_ADDRESS_INCOMPLETE: {
    en: "The customer's city and state do not match a Brazilian municipality, so the NFS-e cannot be issued. Fix the customer's address.",
    ptBR: 'A cidade e o estado do cliente não correspondem a um município brasileiro, então a NFS-e não pode ser emitida. Corrija o endereço do cliente.',
  },
  SERVICE_FISCAL_CODE_MISSING: {
    en: 'The service has no valid LC 116 service code, so the NFS-e cannot be issued. Set the fiscal service type of the service.',
    ptBR: 'O serviço não tem um código de serviço LC 116 válido, então a NFS-e não pode ser emitida. Defina o tipo de serviço fiscal do serviço.',
  },
};

/** Error codes of the issue gate and the warnings it leaves. */
export const AR_INVOICE_ISSUE_ERROR_CODES = [
  'AR_INVOICE_HAS_OPEN_ISSUES',
  'AUTO_APPROVE_SKIPPED_OPEN_ISSUES',
  'INVOICE_HAS_OPEN_ISSUES',
] as const;
export type ArInvoiceIssueErrorCode = (typeof AR_INVOICE_ISSUE_ERROR_CODES)[number];

export const AR_INVOICE_ISSUE_ERROR_MESSAGES: Record<
  ArInvoiceIssueErrorCode,
  { en: string; ptBR: string }
> = {
  AR_INVOICE_HAS_OPEN_ISSUES: {
    en: 'The invoice has open issues and cannot be approved. Fix the issues listed on the invoice, then approve it.',
    ptBR: 'A fatura tem pendências abertas e não pode ser aprovada. Corrija as pendências listadas na fatura e aprove novamente.',
  },
  AUTO_APPROVE_SKIPPED_OPEN_ISSUES: {
    en: 'The invoice was not approved automatically because it has open issues. Fix the issues, then approve it.',
    ptBR: 'A fatura não foi aprovada automaticamente porque tem pendências abertas. Corrija as pendências e aprove a fatura.',
  },
  INVOICE_HAS_OPEN_ISSUES: {
    en: 'The invoice was recorded as a draft with open issues. Send the fixed data again with the same reference, or fix the issues in Alchemy ERP.',
    ptBR: 'A fatura foi registrada como rascunho com pendências abertas. Envie os dados corrigidos com a mesma referência, ou corrija as pendências no Alchemy ERP.',
  },
};

/** One open issue as the API returns it. */
export interface ArInvoiceOpenIssue {
  code: ArInvoiceIssueCode;
  step: ArInvoiceIssueStepCode;
  field: string;
  message: string;
}

/** One open issue as an invoice list row or the invoice detail carries it (ALC-5185). */
export interface ArInvoiceListedIssue {
  code: ArInvoiceIssueCode;
  step: ArInvoiceIssueStepCode;
  field: string;
}

/**
 * The display order of open issues: by code, in `AR_INVOICE_ISSUE_CODES` order, then by field.
 * Issues opened in one transaction share `openedAt`, so it cannot order them stably.
 */
export function sortInvoiceIssues<T extends { code: string; field: string }>(
  rows: readonly T[],
): T[] {
  const rank = (code: string) => {
    const index = (AR_INVOICE_ISSUE_CODES as readonly string[]).indexOf(code);
    return index === -1 ? AR_INVOICE_ISSUE_CODES.length : index;
  };
  return [...rows].sort(
    (a, b) =>
      rank(a.code) - rank(b.code) || a.field.localeCompare(b.field, 'en', { numeric: true }),
  );
}
